import React from 'react';
import { contactDisplayName, type Contact, type Organization, type TaxTreatment } from '../../../types';

type Party = Contact | Organization;

type CustomerBlockNoticeProps = {
  isBlocked?: boolean;
  blockReason?: string | null;
  warningText?: string | null;
  duplicateName?: string | null;
  className?: string;
};

export function CustomerBlockNotice({
  isBlocked,
  blockReason,
  warningText,
  duplicateName,
  className,
}: CustomerBlockNoticeProps) {
  const reason = (blockReason ?? '').trim();
  const warning = (warningText ?? '').trim();
  const duplicate = (duplicateName ?? '').trim();
  if (!isBlocked && !warning && !duplicate) return null;
  return (
    <div
      role="alert"
      className={`rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 ${className ?? ''}`}
    >
      {isBlocked ? (
        <p className="font-semibold">Kunde gesperrt{reason ? `: ${reason}` : '.'}</p>
      ) : null}
      {warning ? <p className={isBlocked ? 'mt-1' : undefined}>{warning}</p> : null}
      {duplicate ? <p className={isBlocked || warning ? 'mt-1' : undefined}>Dublette von {duplicate}.</p> : null}
    </div>
  );
}

function debtorTax(party: Party): TaxTreatment {
  return (
    party.invoice_recipient_organization?.tax_treatment ??
    party.invoice_recipient_contact?.tax_treatment ??
    party.tax_treatment ??
    'standard'
  );
}

function recipientName(party: Party): string {
  const orgName = (party.invoice_recipient_organization?.name ?? '').trim();
  if (orgName) return orgName;
  return contactDisplayName(party.invoice_recipient_contact).trim();
}

function duplicateName(party: Party): string {
  const orgName = (party.duplicate_of_organization?.name ?? '').trim();
  if (orgName) return orgName;
  return contactDisplayName(party.duplicate_of_contact).trim();
}

/** Sperre, Warnung, Empfänger und Reverse-Charge auf der Kundenakte. */
export function CustomerPartySummary({ party, className }: { party: Party; className?: string }) {
  const recipient = recipientName(party);
  const tax = debtorTax(party);
  const duplicate = duplicateName(party);
  const lines = [
    recipient ? `Neue Rechnungen gehen an ${recipient}.` : '',
    tax === 'reverse_charge_13b' ? 'Neue Rechnungen: Reverse-Charge (§13b).' : '',
  ].filter(Boolean);
  const showNotice = Boolean(party.is_blocked) || Boolean((party.warning_text ?? '').trim()) || Boolean(duplicate);
  if (!showNotice && lines.length === 0) return null;
  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      {showNotice ? (
        <CustomerBlockNotice
          isBlocked={party.is_blocked}
          blockReason={party.block_reason}
          warningText={party.warning_text}
          duplicateName={duplicate}
        />
      ) : null}
      {lines.map((line) => (
        <p key={line} className="text-sm text-app-text">
          {line}
        </p>
      ))}
    </div>
  );
}
